import { canReadModule, holdsDocumentClass, recordIsInScope, type GateContext, type RecordFacts } from '../permissions/guards';
import { recordModule, type DocumentClass } from '../permissions/modules';
import { isInternal, isSuperAdmin, type Viewer } from '../permissions/viewer';

/**
 * canOpenFile (piece 9; decisions 0068, 0071, 0074, 0085): one composed
 * function, never an enumerated checklist, failing closed through six ordered
 * steps and returning the grant that allowed the read rather than a boolean.
 * Step 1 is a precondition; authorship widens step 4 and nothing else; the
 * Confidential override at step 6 is widened by neither authorship nor class.
 * A session with no internal employment never enters it and takes
 * canOpenFileExternally instead.
 */
/** 🔒 Decisions 13090, 13210: `Assignment` is the visit's own crew opening its signed sheet or a photo on its notes through the visit's own routes, and nothing else. */
export type FileGrantKind = 'Class' | 'Authorship' | 'Release' | 'ShareLink' | 'ConfidentialityRole' | 'Assignment';
export type FilePermission = 'View' | 'Download';

export interface FileGrant {
  kind: FileGrantKind;
  permission: FilePermission;
}

export type FileRefusal =
  | 'NoInternalEmployment'
  | 'ModulePermission'
  | 'RecordScope'
  | 'DocumentClass'
  | 'NeedsClassification'
  | 'RequiresCover'
  | 'Confidential'
  | 'NotOwnCompany'
  | 'NotCustomerVisible';

export type FileAccessAnswer = { allowed: true; grant: FileGrant } | { allowed: false; step: number; refusedAt: FileRefusal; message: string };

/** What the gate reads about the file: the stored class, the override, version 1's uploader and the record it sits on. */
export interface FileFacts {
  fileId: string;
  classification: DocumentClass | null;
  confidentialityRoleId: string | null;
  /** Derived from version 1, never stored on File (task #227). */
  uploadedByPersonId: string;
  /** The tagged record's facts, or null for an untagged file in the Documents Inbox. */
  record: RecordFacts | null;
  /** Every Company on the path upward from the tagged record; the portal's structural gate (decision 0073). */
  pathCompanyIds: readonly string[];
  /** FileType.customerMayDownload: the look-or-keep answer on the class path (decision 0085). */
  customerMayDownload: boolean;
}

/** requiresCover, evaluated at read time against the record's Company or Deal and never stamped (decision 0071). */
export interface CoverFacts {
  requiresCover: boolean;
  counterpartyName: string | null;
  /** The viewer holds a current person-scoped agreement, so they are covered. */
  viewerCovered: boolean;
}

/** Step 6's two facts: the named ConfidentialityRole and a current personal agreement (decision 0068). */
export interface ConfidentialFacts {
  viewerHoldsRole: boolean;
  viewerHasCurrentPersonalAgreement: boolean;
}

/** The portal viewer's release: the permission of an open FileRelease naming their own Company, or null. */
export interface ReleaseFacts {
  releasedToViewerCompany: FilePermission | null;
}

export interface FileGateContext extends GateContext {
  /** The seeded Admin role's id; an untagged file is visible to its uploader and Admin (piece 9). */
  adminRoleId?: string;
  /**
   * 🔒 Decisions 13090, 13210: the visit's sheet or picture route found the viewer on the visit's assignment (decision
   * 5572) and the file is that visit's signed sheet or a photo on its notes, so steps 2 to 4 pass on it with an
   * `Assignment` grant. 🔨 No other caller sets it: the file route takes the six steps as they stand.
   */
  onAssignment?: boolean;
}

/** The classes a portal viewer reaches by class alone; every other class reaches them only by release (decision 0073). */
export const CUSTOMER_VISIBLE_CLASSES: readonly DocumentClass[] = ['Public', 'Commercial', 'Legal'];

export const COVER_REFUSAL = (counterparty: string): string => `${counterparty} requires an NDA. You are not currently covered. Request cover.`;

const sandboxGrant: FileGrant = { kind: 'Class', permission: 'Download' };

function refuse(step: number, refusedAt: FileRefusal, message: string): FileAccessAnswer {
  return { allowed: false, step, refusedAt, message };
}

/**
 * Step 6, module-private by rule: called by canOpenFile and by no other caller
 * (piece 8's register). It requires the named role and a current personal
 * agreement, and neither authorship nor class widens it.
 */
function canViewConfidential(facts: ConfidentialFacts): boolean {
  return facts.viewerHoldsRole && facts.viewerHasCurrentPersonalAgreement;
}

/** 🔒 Decisions 13090, 13210: the grant the visit's sheet and picture routes record for a worker its assignment names. */
const ASSIGNMENT_GRANT: FileGrant = { kind: 'Assignment', permission: 'Download' };

/**
 * Steps 2 to 4 on a tagged file, module-private: the record's module, its scope and the document class, answering
 * the grant step 4 allowed or the refusal. Authorship widens step 4 and nothing else (decision 0074).
 */
function recordSteps(viewer: Viewer, record: RecordFacts, classification: DocumentClass | null, ctx: FileGateContext, authorOrAdmin: boolean, author: boolean): FileGrant | FileAccessAnswer {
  // 2. Module permission on the owning record, through the one resolver.
  if (!canReadModule(viewer, recordModule(record.recordType), ctx)) return refuse(2, 'ModulePermission', 'No permission on the module the record belongs to');
  // 3. Record scope.
  if (!recordIsInScope(viewer, record, ctx)) return refuse(3, 'RecordScope', 'The record is outside the viewer’s scope');
  // 4. Document class; authorship widens this step and nothing else (decision 0074).
  if (classification === null) {
    if (!authorOrAdmin) return refuse(4, 'NeedsClassification', 'The file awaits classification');
    return { kind: 'Authorship', permission: 'Download' };
  }
  if (holdsDocumentClass(viewer, classification, ctx)) return { kind: 'Class', permission: 'Download' };
  if (author) return { kind: 'Authorship', permission: 'Download' };
  return refuse(4, 'DocumentClass', `The ${classification} class is not held`);
}

export function canOpenFile(viewer: Viewer, file: FileFacts, cover: CoverFacts, confidential: ConfidentialFacts, ctx: FileGateContext): FileAccessAnswer {
  if (ctx.mode === 'sandbox') return { allowed: true, grant: sandboxGrant };
  // 1. An active internal EmploymentRecord is a precondition, not a term in a sum.
  if (!isInternal(viewer)) return refuse(1, 'NoInternalEmployment', 'An internal employment is required to open a file');
  const admin = ctx.adminRoleId !== undefined && isSuperAdmin(viewer, ctx.adminRoleId);
  const author = file.uploadedByPersonId === viewer.personId;
  if (file.record === null) {
    // An untagged file reaches no module and fails closed to its uploader and Admin.
    if (author || admin) return { allowed: true, grant: { kind: 'Authorship', permission: 'Download' } };
    return refuse(2, 'NeedsClassification', 'The file is tagged to no record and is visible to its uploader and Admin alone');
  }
  // 🔒 Decisions 13090, 13210: the visit's own crew on its signed sheet or a photo on its notes passes steps 2 to 4 on the assignment; steps 5 and 6 still hold.
  const reached = ctx.onAssignment === true ? ASSIGNMENT_GRANT : recordSteps(viewer, file.record, file.classification, ctx, author || admin, author);
  if ('allowed' in reached) return reached;
  let grant: FileGrant = reached;
  // 5. requiresCover, read live; authorship never beats it.
  if (cover.requiresCover && !cover.viewerCovered) return refuse(5, 'RequiresCover', COVER_REFUSAL(cover.counterpartyName ?? 'The counterparty'));
  // 6. The Confidential override; authorship and class never beat it either.
  if (file.confidentialityRoleId !== null) {
    if (!canViewConfidential(confidential)) return refuse(6, 'Confidential', 'The file is Confidential and the viewer holds no covering role');
    grant = { kind: 'ConfidentialityRole', permission: 'Download' };
  }
  return { allowed: true, grant };
}

/**
 * The portal gate (decisions 0068, 0073): the viewer's own Company must sit on
 * the path upward from the record, then the class is customer-visible or the
 * file was released to them. Nothing else is consulted, and a refusal says
 * nothing about what is withheld.
 */
export function canOpenFileExternally(viewer: Viewer, file: FileFacts, release: ReleaseFacts, ctx: GateContext): FileAccessAnswer {
  if (ctx.mode === 'sandbox') return { allowed: true, grant: sandboxGrant };
  if (!viewer.employmentActive) return refuse(1, 'NotOwnCompany', 'Not found');
  if (!file.pathCompanyIds.includes(viewer.companyId)) return refuse(1, 'NotOwnCompany', 'Not found');
  if (file.classification !== null && file.confidentialityRoleId === null && CUSTOMER_VISIBLE_CLASSES.includes(file.classification)) {
    return { allowed: true, grant: { kind: 'Class', permission: file.customerMayDownload ? 'Download' : 'View' } };
  }
  if (release.releasedToViewerCompany !== null) return { allowed: true, grant: { kind: 'Release', permission: release.releasedToViewerCompany } };
  return refuse(2, 'NotCustomerVisible', 'Not found');
}

/**
 * 🔨 A share link's own grant never widens the gate's (decisions 0085, 0191):
 * the narrower of the two permissions wins, so a reader holding a View on the
 * file who mints a Download link still gets a view. The kind is the link's,
 * because that is what the access line records; the permission is the lower of
 * the two. 💰 It replaced the gate's grant outright until it was found in
 * review, 2026-09-18.
 */
export function narrowedGrant(gateGrant: FileGrant, override: FileGrant | null | undefined): FileGrant {
  if (!override) return gateGrant;
  return { kind: override.kind, permission: gateGrant.permission === 'Download' && override.permission === 'Download' ? 'Download' : 'View' };
}

/** The fixed allowlist: PDF and raster images render inline; everything else, .html and .svg included, is an attachment (piece 9). */
export const INLINE_MIME_ALLOWLIST: readonly string[] = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp'];

export function inlineSafe(mimeType: string): boolean {
  return INLINE_MIME_ALLOWLIST.includes(mimeType.toLowerCase().split(';')[0]!.trim());
}

export type Disposition = 'inline' | 'attachment' | 'refuse';

/**
 * Which element draws a served file, for the viewer that fetches its own bytes
 * (decision 0549). A PDF draws in a frame and a raster image in an image
 * element; every other type draws in neither, whatever grant the reader holds.
 * 🔨 The plug-in elements are not on this list at all: the deployed policy
 * carries `object-src 'none'`, so an `object` or an `embed` never loads and the
 * reader meets the fallback text instead of the file (decision 0549).
 */
export type InlineRender = 'frame' | 'image';

export function inlineRenderFor(mimeType: string): InlineRender | null {
  const bare = mimeType.toLowerCase().split(';')[0]!.trim();
  if (!inlineSafe(bare)) return null;
  return bare === 'application/pdf' ? 'frame' : 'image';
}

/**
 * The grant and the allowlist compose: the grant decides whether inline is
 * permitted at all, the stored mimeType decides whether inline is safe, and
 * attachment is the answer whenever either says no. A View-only grant on a
 * type off the allowlist serves nothing at all (decision 0085).
 */
export function dispositionFor(grant: FileGrant, mimeType: string, wantsDownload: boolean): Disposition {
  const safe = inlineSafe(mimeType);
  if (grant.permission === 'View') return safe && !wantsDownload ? 'inline' : 'refuse';
  return safe && !wantsDownload ? 'inline' : 'attachment';
}

/** "Superior Panel Co · j.ramirez@superiorpanel.com · 2026-08-31 14:22", with the optional configured line under it. */
export function watermarkText(viewer: { companyName: string; email: string }, at: Date, extraLine: string | null = null): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  const stamp = `${at.getUTCFullYear()}-${pad(at.getUTCMonth() + 1)}-${pad(at.getUTCDate())} ${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}`;
  const line = `${viewer.companyName} · ${viewer.email} · ${stamp}`;
  return extraLine && extraLine.trim().length > 0 ? `${line}\n${extraLine.trim()}` : line;
}

/** Every covered document carries a marker naming the counterparty, derived from the agreement and never stored (decision 0071). */
export function coverMarker(cover: Pick<CoverFacts, 'requiresCover' | 'counterpartyName'>): string | null {
  if (!cover.requiresCover) return null;
  return `Covered by ${cover.counterpartyName ?? 'an NDA'}`;
}

/** What the two share-link guards read: the link's own row and the file's trash state (decisions 0085, 0191; piece 21). */
export interface ShareLinkFacts {
  permission: FilePermission;
  revokedAt: Date | string | null;
  expiresAt: Date | string | null;
}

export interface SharedFileFacts {
  deletedAt: Date | string | null;
  permanentlyDeletedAt: Date | string | null;
}

export type SharedFileRefusal = 'Revoked' | 'Expired' | 'Trashed' | 'ViewOnly';
export type SharedFileAnswer = { allowed: true; grant: FileGrant } | { allowed: false; refusedAt: SharedFileRefusal; message: string };

/**
 * canOpenSharedFile(token, file): the inline view a share link grants. A
 * revoked or expired link opens nothing, and neither does a trashed file;
 * the composed canOpenFile still runs for the signed-in person on the same
 * open (decision 0191), which this guard does not replace.
 */
export function canOpenSharedFile(link: ShareLinkFacts, file: SharedFileFacts, now: Date = new Date()): SharedFileAnswer {
  if (link.revokedAt) return { allowed: false, refusedAt: 'Revoked', message: `the share link was revoked on ${new Date(link.revokedAt).toISOString()}` };
  if (link.expiresAt && new Date(link.expiresAt).getTime() <= now.getTime()) return { allowed: false, refusedAt: 'Expired', message: `the share link expired on ${new Date(link.expiresAt).toISOString()}` };
  if (file.deletedAt || file.permanentlyDeletedAt) return { allowed: false, refusedAt: 'Trashed', message: 'the file is in the trash; a share link opens nothing until it is restored' };
  return { allowed: true, grant: { kind: 'ShareLink', permission: link.permission } };
}

/**
 * 🔨 canDownloadSharedFile(token, file) decides the download route alone
 * (decision 0085; piece 21): a View token is refused at the endpoint, not
 * merely left without a Download button, and a Download token gets both.
 */
export function canDownloadSharedFile(link: ShareLinkFacts, file: SharedFileFacts, now: Date = new Date()): SharedFileAnswer {
  const open = canOpenSharedFile(link, file, now);
  if (!open.allowed) return open;
  if (link.permission !== 'Download') return { allowed: false, refusedAt: 'ViewOnly', message: 'the share link permits viewing and no copy is handed over' };
  return open;
}
